import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon, ArrowRightEndOnRectangleIcon, ArrowLeftStartOnRectangleIcon, ExclamationTriangleIcon, XMarkIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { usePermissions } from '../../auth';
import Modal from '../../components/common/Modal';
import ImageCarousel from '../../components/common/ImageCarousel';
import { TableCard, TableSkeleton, TableEmpty, TableError, SearchInput, Pager, usePagination, PageSizeSelect, FilterBar, TH, TD, RECORD_PAGE_SIZE, useSort, SortableTH, timeKey, CONTROL, FILTER_BTN, FOCUS_RING, FilterField, FilterCount, FilterActions } from './tableUi';
import { PresenceTag } from './AttendanceTags';
import { todayStr } from './statusUtils';
import { detectPatternAnomalies, anomalyIndexMap, anomalyLabel, missingLabel, type PatternAnomaly } from './attendanceAnomalies';

/**
 * Unified in/out timeline for employees + registered non-employees, mirroring the ANPR
 * vehicle timeline (timeline1/2.png) but for people. One expandable row per identity:
 * counts, first/last seen, paired in→out duration, current status, an in/out dot pattern,
 * and — on expand — a vertical event timeline with face photos and "Inside for …" chips.
 *
 * Theme: IN = green (success), OUT = red (destructive), ANOMALY = orange (brand). A day
 * should read green-red-green-red; orange marks where that alternation breaks because the
 * AI module missed a face (see attendanceAnomalies.ts). Duration is a visibility metric
 * here; the Presence / Payroll tabs remain the authoritative attendance numbers.
 */

interface TimelineEvent {
  seen_at: string;
  inout: string;
  camera_id: string | null;
  event_id: string | null;
  snapshot_url: string | null;
  confidence: number | null;
}
interface TimelineRow {
  type: 'employee' | 'person';
  id: string;
  person_id: string | null;
  name: string;
  employee_code: string | null;
  label: string | null;
  in_count: number;
  out_count: number;
  event_count: number;
  first_seen: string | null;
  last_seen: string | null;
  total_inside_minutes: number;
  status: 'in' | 'out';
  /** Official day status for employees (present | half_day | partial | absent); absent for non-employees. */
  attendance_status?: string;
  pattern: string[];
  events: TimelineEvent[];
}

function fmtDateTime(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function fmtClock(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
/** Compact duration from minutes → "1d 4h 5m" / "24m" / "0m". */
function fmtDur(totalMinutes?: number | null): string {
  const m = Math.max(0, Math.round(totalMinutes || 0));
  if (m === 0) return '0m';
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  return [d ? `${d}d` : '', h ? `${h}h` : '', mm || (!d && !h) ? `${mm}m` : ''].filter(Boolean).join(' ');
}
/** Compact duration from seconds → "13m 50s" / "31m 37s" (for expanded "Inside for …" chips). */
function fmtDurSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', `${sec}s`].filter(Boolean).join(' ');
}

// In/out colors are driven straight off the base CSS design tokens (defined in index.css),
// NOT Tailwind color names: `success`/`info`/`warning` are not mapped to Tailwind color
// utilities in this project, so `bg-success` etc. generate no CSS and render invisible.
// Referencing the vars directly guarantees the IN (green) and OUT (orange) dots always show.
const IN_COLOR = 'var(--success)';        // green  — entry
const OUT_COLOR = 'var(--destructive)';   // red    — exit
// Orange, and its own token rather than --brand: brand is a vermilion that reads as red
// next to the exit dot, which is the one distinction this column exists to make.
const ANOMALY_COLOR = 'var(--anomaly)';   // orange — broken alternation (a missed event)
const UNK_COLOR = 'var(--muted-foreground)';
const inoutColor = (io: string) => (io === 'in' ? IN_COLOR : io === 'out' ? OUT_COLOR : UNK_COLOR);
/** Soft tinted badge background + solid text, from a CSS-var color. */
const tint = (c: string) => ({ backgroundColor: `color-mix(in srgb, ${c} 15%, transparent)`, color: c });

/** Columns the timeline can be ordered by — name, and the two ends of each day. */
const TIMELINE_SORTS = {
  name: (r: TimelineRow) => r.name || '',
  first_seen: (r: TimelineRow) => timeKey(r.first_seen),
  last_seen: (r: TimelineRow) => timeKey(r.last_seen),
};

export interface TimelineTabProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
}

export default function TimelineTab({ nodeKey = '' }: TimelineTabProps) {
  const { attendance } = useApi();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The repair hand-off reads the unknown-face feed. Without access to it the anomaly is
  // still worth showing — the operator just can't be the one to fix it, so we surface the
  // gap and omit the dead-end button rather than routing them to an empty page.
  const canOpenFaceLog = hasPermission('ai_events.unknown_faces') || hasPermission('ai_events.view');
  // The sighting detail is AI-event data (and its images come from the event-image proxy), so
  // without ai_events.view the sighting stays inert text rather than opening a panel the
  // reader isn't allowed to see.
  const canOpenEventDetail = hasPermission('ai_events.view');
  const [rows, setRows] = useState<TimelineRow[]>([]);
  // `?date=` lets the round trip through the face log come back to the day the operator
  // was repairing, rather than dumping them on today.
  const [date, setDate] = useState(params.get('date') || todayStr());
  const [cameraId, setCameraId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [cameras, setCameras] = useState<{ id: string; name: string }[]>([]);
  /** The sighting whose detail is open over the table, with the identity it belongs to. */
  const [sighting, setSighting] = useState<{ ev: TimelineEvent; personName: string } | null>(null);

  // Camera list for the filter dropdown + camera_id → name resolution in the expanded view.
  useEffect(() => {
    (async () => {
      try {
        const list = await attendance.listCameras();
        setCameras((Array.isArray(list) ? list : []).map((c: any) => ({ id: c.id || c.camera_id, name: c.name || c.location || c.id })));
      } catch (e) { console.error('camera list load failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, nodeKey]);

  const cameraName = useCallback((id?: string | null) => cameras.find((c) => c.id === id)?.name || id || '—', [cameras]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendance.timeline({ date, camera_id: cameraId || undefined });
      setRows(res?.rows || []);
      setError('');
    } catch (e: any) {
      console.error('timeline fetch failed', e);
      setRows([]);
      // Recorded rather than swallowed: an empty timeline and a failed request used to
      // render identically, and "no activity today" is a costly thing to get wrong.
      setError(e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, date, cameraId, nodeKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setExpanded(null); }, [date, cameraId, search]);

  // Search by registered/employee name is instant + client-side over the loaded day.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || '').toLowerCase().includes(q) || (r.employee_code || '').toLowerCase().includes(q));
  }, [rows, search]);

  const { sorted, sort, toggleSort } = useSort(filtered, TIMELINE_SORTS);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(sorted, RECORD_PAGE_SIZE);

  // Any filter or reorder starts the reader back at the top of the list — after a sort,
  // "page 3" refers to entirely different people.
  useEffect(() => { setPage(1); }, [date, cameraId, search, sort, setPage]);

  const insideCount = rows.filter((r) => r.status === 'in').length;

  // Anomalies are derived once per load and keyed by identity: the summary chip, the dot
  // colours and the expanded placeholders must all be reading the same verdict.
  const anomaliesByRow = useMemo<Map<string, PatternAnomaly[]>>(() => {
    const m = new Map<string, PatternAnomaly[]>();
    for (const r of rows) m.set(r.id, detectPatternAnomalies(r.pattern || []));
    return m;
  }, [rows]);
  const flaggedCount = useMemo(() => {
    let n = 0;
    anomaliesByRow.forEach((a: PatternAnomaly[]) => { if (a.length) n += 1; });
    return n;
  }, [anomaliesByRow]);

  /**
   * Jump to the face log for the window a missing event should have fallen in, so the
   * operator can link the unknown face to this identity. Coming back re-runs load() and
   * the repaired pattern replaces the orange dot.
   */
  const openFaceLog = useCallback((row: TimelineRow, a: PatternAnomaly) => {
    const before = row.events[a.index];
    const after = a.afterIndex != null ? row.events[a.afterIndex] : null;
    const qs = new URLSearchParams({
      missing: a.missing,
      name: row.name || '',
      date,
      returnTo: `/attendance/timeline?date=${date}`,
    });
    if (after?.seen_at) qs.set('from', after.seen_at);
    if (before?.seen_at) qs.set('to', before.seen_at);
    navigate(`/attendance/unknown-faces?${qs.toString()}`);
  }, [navigate, date]);

  /**
   * Show what the AI module recorded for a sighting, in a modal on this page.
   *
   * It used to route to `/reports/face?event_id=…`, which cost the operator the whole
   * timeline: the day, the camera filter, the search and the expanded row all had to be
   * rebuilt on the way back, and the browser's Back button landed on the unfiltered report.
   * Reading one sighting is a detail *of* the row already on screen, so it opens over it.
   * The full report is still one click away in the modal for the rest of the record
   * (similarity score, the match used, the register action).
   */
  const openSighting = useCallback((ev: TimelineEvent, row: TimelineRow) => {
    if (!ev.event_id) return;
    setSighting({ ev, personName: row.name });
  }, []);

  // Defaults are today / all cameras / no search — "Clear filters" only offers itself
  // when at least one of them has been moved off its default.
  const isToday = date === todayStr();
  const filtersActive = !isToday || !!cameraId || !!search;
  const clearFilters = () => { setDate(todayStr()); setCameraId(''); setSearch(''); };

  return (
    <div>
      {/* ── Filters ── */}
      <FilterBar>
        <FilterField label="Date">
          {/* `max` is TODAY, not the work date the response came back with. That was
              whatever day was being viewed, so selecting the 10th re-rendered the picker
              with max=the 10th — every later day greyed out, and the only way back was the
              Today button. The ceiling is a property of the calendar, not of the day being
              viewed. */}
          <input type="date" value={date} max={todayStr()}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Work date"
            className={CONTROL} />
        </FilterField>
        {!isToday && (
          <button onClick={() => setDate(todayStr())} className={FILTER_BTN}>Today</button>
        )}

        {/* "All cameras" names the filter, so a separate label would just repeat it. */}
        <select value={cameraId} onChange={(e) => setCameraId(e.target.value)}
          aria-label="Filter by camera"
          className={`${CONTROL} max-w-[180px]`}>
          <option value="">All cameras</option>
          {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <SearchInput value={search} onChange={setSearch} placeholder="Search name or code…" label="Search people" />

        {filtersActive && (
          <button onClick={clearFilters} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        {/* Readouts, in severity order: how many are in, then how many need attention. */}
        <FilterCount tone="accent">{insideCount} inside now</FilterCount>
        {flaggedCount > 0 && (
          <span
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[12px] font-medium"
            style={tint(ANOMALY_COLOR)}
            title="These identities have an in/out sequence that doesn't alternate — expand a row to see where an event is missing"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {flaggedCount} with gaps
          </span>
        )}
        <FilterCount>{search ? `${filtered.length} of ${rows.length}` : `${rows.length}`} tracked</FilterCount>

        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button onClick={load} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </FilterActions>
      </FilterBar>

      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError message={error} onRetry={load}>Couldn&apos;t load the timeline</TableError>
        ) : rows.length === 0 ? (
          // The camera filter is applied server-side, so an empty day may just mean
          // "nothing on that camera" — say which.
          <TableEmpty>No in/out activity for this day{cameraId ? ' on this camera' : ''}.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No one matches these filters.</TableEmpty>
        ) : (
          <>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className={`${TH} w-8`}></th>
                  <th className={`${TH} w-10`}>#</th>
                  <SortableTH label="Name" columnKey="name" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Pattern</th>
                  <th className={TH}>In</th>
                  <th className={TH}>Out</th>
                  <th className={TH}>Events</th>
                  <SortableTH label="First seen" columnKey="first_seen" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Last seen" columnKey="last_seen" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Duration</th>
                  <th className={TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r, i) => {
                  const open = expanded === r.id;
                  return (
                    <RowGroup
                      key={r.id}
                      row={r}
                      sn={pagerProps.from + i}
                      open={open}
                      onToggle={() => setExpanded(open ? null : r.id)}
                      cameraName={cameraName}
                      anomalies={anomaliesByRow.get(r.id) || []}
                      onFindMissing={canOpenFaceLog ? (a: PatternAnomaly) => openFaceLog(r, a) : undefined}
                      onOpenEvent={canOpenEventDetail ? (ev: TimelineEvent) => openSighting(ev, r) : undefined}
                    />
                  );
                })}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>

      {/* One sighting, over the timeline rather than instead of it. */}
      <Modal
        open={!!sighting}
        onClose={() => setSighting(null)}
        title="Sighting Details"
        subtitle={sighting ? `${sighting.personName} · ${fmtClock(sighting.ev.seen_at)}` : undefined}
        // Matches the alert detail modal so a snapshot gets the same width in both.
        size="xl"
        footer={sighting?.ev.event_id && (
          <button
            type="button"
            onClick={() => navigate(`/reports/face?event_id=${encodeURIComponent(sighting.ev.event_id!)}`)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium text-foreground hover:bg-muted transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Open in Face Recognition report
          </button>
        )}
      >
        {sighting && (
          <SightingDetail
            key={sighting.ev.event_id || sighting.ev.seen_at}
            ev={sighting.ev}
            personName={sighting.personName}
            cameraName={cameraName}
          />
        )}
      </Modal>
    </div>
  );
}

/**
 * Every image type the AI module files for a face event, widest first — the full frame is the
 * one that answers "what was actually happening", the crops are what the match was made on.
 * Each is a probe: a type this event doesn't have simply resolves null and is skipped.
 */
const SIGHTING_IMAGE_TYPES: { type: string; label: string }[] = [
  { type: 'fullFrame', label: 'Full frame' },
  { type: 'faceCrop', label: 'Face crop' },
  { type: 'alignedFace', label: 'Aligned face' },
];

/** What the AI module recorded for one sighting: its snapshots and the surrounding facts. */
function SightingDetail({ ev, personName, cameraName }: {
  ev: TimelineEvent;
  personName: string;
  cameraName: (id?: string | null) => string;
}) {
  const { attendance } = useApi();
  const [images, setImages] = useState<{ label: string; url: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Sequential, not parallel: on the cloud each of these is a base64 round trip through the
  // node tunnel, and the full frame is the one worth having first. These are `data:` URLs, so
  // unlike the alert detail's blobs there is nothing to revoke.
  useEffect(() => {
    let alive = true;
    if (!ev.event_id) { setImages([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      const out: { label: string; url: string }[] = [];
      for (const { type, label } of SIGHTING_IMAGE_TYPES) {
        const url = await attendance.eventImageDataUrl(ev.event_id!, type);
        if (!alive) return;
        if (url) out.push({ label, url });
      }
      if (alive) { setImages(out); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [attendance, ev.event_id]);

  const direction = ev.inout === 'in' ? 'Entry' : ev.inout === 'out' ? 'Exit' : 'Seen';

  return (
    <div className="px-5 py-4 space-y-4">
      {loading ? (
        <div className="aspect-video rounded-lg bg-muted animate-pulse" />
      ) : images.length > 0 ? (
        // Same presentation as the alert detail: one full-width frame at a time with a
        // thumbnail strip, rather than a grid of small tiles — a face crop is unreadable at
        // tile size, which is the whole reason to open this.
        <ImageCarousel images={images} />
      ) : (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-6 text-center text-[12px] text-muted-foreground">
          No snapshots stored for this sighting.
        </div>
      )}

      <div>
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sighting Information</h3>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailField label="Identity" value={personName} />
          <DetailField
            label="Direction"
            value={
              <span
                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                style={tint(inoutColor(ev.inout))}
              >
                {direction}
              </span>
            }
          />
          <DetailField label="Seen at" value={fmtDateTime(ev.seen_at)} />
          <DetailField label="Camera" value={cameraName(ev.camera_id)} />
          <DetailField label="Confidence" value={ev.confidence != null ? Number(ev.confidence).toFixed(2) : '—'} />
          <DetailField label="Event ID" value={<span className="font-mono text-[12px]">{ev.event_id || '—'}</span>} />
        </dl>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-[13px] font-medium text-foreground">{value}</dd>
    </div>
  );
}

/** One identity: the summary row plus (when open) its expanded event timeline. */
function RowGroup({ row, sn, open, onToggle, cameraName, anomalies, onFindMissing, onOpenEvent }: {
  row: TimelineRow; sn: number; open: boolean; onToggle: () => void;
  cameraName: (id?: string | null) => string;
  anomalies: PatternAnomaly[];
  /** Absent when the reader can't read the unknown-face feed — the jump is then omitted. */
  onFindMissing?: (a: PatternAnomaly) => void;
  /** Absent when the reader can't read AI events — sightings then render as plain rows. */
  onOpenEvent?: (ev: TimelineEvent) => void;
}) {
  return (
    <>
      <tr className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer" onClick={onToggle}>
        <td className={TD}>
          <ChevronRightIcon className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
        </td>
        <td className={`${TD} text-muted-foreground`}>{sn}</td>
        <td className={TD}>
          <div className="font-medium text-foreground">{row.name}</div>
          <div className="text-xs text-muted-foreground">
            {row.type === 'employee'
              ? (row.employee_code || 'Employee')
              : <span className="capitalize">{row.label || 'Registered'}</span>}
          </div>
        </td>
        <td className={TD}><PatternDots pattern={row.pattern} anomalies={anomalies} /></td>
        <td className={TD}><CountPill n={row.in_count} tone="in" /></td>
        <td className={TD}><CountPill n={row.out_count} tone="out" /></td>
        <td className={TD}><CountPill n={row.event_count} tone="neutral" /></td>
        <td className={`${TD} whitespace-nowrap text-xs text-muted-foreground`}>{fmtDateTime(row.first_seen)}</td>
        <td className={`${TD} whitespace-nowrap text-xs text-muted-foreground`}>{fmtDateTime(row.last_seen)}</td>
        <td className={`${TD} whitespace-nowrap`}>{fmtDur(row.total_inside_minutes)}</td>
        <td className={TD}>
          <div className="flex items-center gap-1.5">
            <StatusBadge status={row.status} />
            {/* Daily view → Present / Absent, matching the Presence board. */}
            {row.attendance_status && <PresenceTag status={row.attendance_status} />}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/20 border-b border-border">
          <td colSpan={11} className="px-4 py-4">
            <EventTimeline
              events={row.events}
              cameraName={cameraName}
              anomalies={anomalies}
              onFindMissing={onFindMissing}
              onOpenEvent={onOpenEvent}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/** Connected-dot representation of the in/out sequence: IN = green, OUT = red, unknown =
 *  gray, dots joined by a short rail (reference: the PATTERN column in timeline1.png).
 *  Wraps to a new line every PER_ROW dots so long sequences stack instead of overflowing.
 *
 *  Dots that break alternation render orange with a halo ring. The ring matters as much as
 *  the colour: red and orange are the pair most red-green colourblind readers lose, so the
 *  anomaly has to be identifiable by shape too. */
function PatternDots({ pattern, anomalies }: { pattern: string[]; anomalies: PatternAnomaly[] }) {
  const PER_ROW = 10;
  const flagged = anomalyIndexMap(anomalies);
  // Split into rows of PER_ROW; each row keeps its own connector rail between dots.
  const rows: string[][] = [];
  for (let i = 0; i < pattern.length; i += PER_ROW) rows.push(pattern.slice(i, i + PER_ROW));
  return (
    <div className="flex items-start gap-2">
      <div className="flex flex-col gap-1">
        {rows.map((row, ri) => (
          <div key={ri} className="flex items-center">
            {row.map((p, i) => {
              const index = ri * PER_ROW + i;
              const anomaly = flagged.get(index);
              return (
                <div key={i} className="flex items-center">
                  {i > 0 && <span className="h-px w-2.5 shrink-0" style={{ backgroundColor: 'var(--border)' }} />}
                  <span
                    title={anomaly ? anomalyLabel(anomaly) : p}
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={anomaly
                      ? { backgroundColor: ANOMALY_COLOR, boxShadow: `0 0 0 2px color-mix(in srgb, ${ANOMALY_COLOR} 35%, transparent)` }
                      : { backgroundColor: inoutColor(p) }}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {anomalies.length > 0 && (
        <span
          className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-semibold shrink-0"
          style={tint(ANOMALY_COLOR)}
          title="Expand this row to see where an event is missing"
        >
          <ExclamationTriangleIcon className="h-3 w-3" />
          {anomalies.length}
        </span>
      )}
    </div>
  );
}

function CountPill({ n, tone }: { n: number; tone: 'in' | 'out' | 'neutral' }) {
  const base = 'inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold';
  if (tone === 'neutral') return <span className={`${base} bg-muted text-muted-foreground`}>{n}</span>;
  return <span className={base} style={tint(tone === 'in' ? IN_COLOR : OUT_COLOR)}>{n}</span>;
}

function StatusBadge({ status }: { status: 'in' | 'out' }) {
  return status === 'in'
    ? <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={tint(IN_COLOR)}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: IN_COLOR }} /> Inside</span>
    : <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase bg-muted text-muted-foreground">Out</span>;
}

/**
 * One entry in the expanded view: either a real sighting, or the placeholder standing in
 * for a sighting the AI module never attributed to this identity.
 */
type TimelineNode =
  | { kind: 'event'; ev: TimelineEvent; index: number }
  | { kind: 'missing'; anomaly: PatternAnomaly; from: TimelineEvent | null; to: TimelineEvent };

/** Vertical event timeline (reference: timeline2.png), with "Inside for …" chips between an in and the following out. */
function EventTimeline({ events, cameraName, anomalies, onFindMissing, onOpenEvent }: {
  events: TimelineEvent[]; cameraName: (id?: string | null) => string;
  anomalies: PatternAnomaly[];
  onFindMissing?: (a: PatternAnomaly) => void;
  onOpenEvent?: (ev: TimelineEvent) => void;
}) {
  if (!events.length) return <div className="text-sm text-muted-foreground">No events.</div>;
  const flagged = anomalyIndexMap(anomalies);

  // "Inside for …" pairs an entry with the exit immediately after it. Computed over the
  // raw chronological events, before placeholders are interleaved, so inserting a gap
  // marker can never change a duration.
  const insideChips = new Map<number, string>();
  for (let i = 1; i < events.length; i++) {
    if (events[i].inout === 'out' && events[i - 1].inout === 'in') {
      const secs = (new Date(events[i].seen_at).getTime() - new Date(events[i - 1].seen_at).getTime()) / 1000;
      if (secs > 0) insideChips.set(i, fmtDurSeconds(secs));
    }
  }

  // Built chronologically so each placeholder slots in just before the event that exposed
  // it, then reversed for the newest-first display the reference uses.
  const chronological: TimelineNode[] = [];
  events.forEach((ev, index) => {
    const anomaly = flagged.get(index);
    if (anomaly) {
      chronological.push({
        kind: 'missing',
        anomaly,
        from: anomaly.afterIndex != null ? events[anomaly.afterIndex] : null,
        to: ev,
      });
    }
    chronological.push({ kind: 'event', ev, index });
  });
  const ordered = [...chronological].reverse();

  return (
    // Continuous vertical rail behind the row dots (reference: timeline2.png left edge).
    <div className="relative">
      <span className="absolute left-[6px] top-7 bottom-7 w-px" style={{ backgroundColor: 'var(--border)' }} />
      {ordered.map((node, i) => {
        if (node.kind === 'missing') {
          return (
            <MissingEventNode
              key={`missing-${node.anomaly.index}`}
              anomaly={node.anomaly}
              from={node.from}
              to={node.to}
              onFind={onFindMissing ? () => onFindMissing(node.anomaly) : undefined}
            />
          );
        }
        const { ev, index } = node;
        const insideChip = insideChips.get(index) || null;
        // Only a sighting the AI module actually filed has a detail to open: without an
        // event_id there is no record to fetch, and no images to fetch it by.
        const openable = !!onOpenEvent && !!ev.event_id;
        const body = (
          <>
            <EventPhoto eventId={ev.event_id} />
            <div className="flex items-center gap-2 min-w-0">
              {ev.inout === 'in' && <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={tint(IN_COLOR)}><ArrowRightEndOnRectangleIcon className="h-3 w-3" /> Entry</span>}
              {ev.inout === 'out' && <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={tint(OUT_COLOR)}><ArrowLeftStartOnRectangleIcon className="h-3 w-3" /> Exit</span>}
              {ev.inout !== 'in' && ev.inout !== 'out' && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-muted text-muted-foreground">Seen</span>}
              <span className="text-sm font-medium text-foreground whitespace-nowrap">{fmtClock(ev.seen_at)}</span>
              <span className="text-xs text-muted-foreground truncate">· {cameraName(ev.camera_id)}</span>
              {ev.confidence != null && <span className="text-[10px] text-muted-foreground">· {Number(ev.confidence).toFixed(2)}</span>}
              {openable && (
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              )}
            </div>
          </>
        );
        return (
          <div key={`event-${index}-${i}`}>
            <div className="relative">
              {/* Opaque dot sits ON the rail (covers the 1px line) so it reads as a timeline node.
                  Kept outside the button: it belongs to the rail, not to the clickable record. */}
              <span className="absolute left-[2px] top-1/2 -translate-y-1/2 z-10 h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: inoutColor(ev.inout) }} />
              {openable ? (
                // A button, not a div with onClick: this opens a dialog, so it has to be
                // reachable and activatable from the keyboard like any other control.
                <button
                  type="button"
                  onClick={() => onOpenEvent!(ev)}
                  title="View this sighting's details"
                  className={`group flex w-full items-center gap-3 rounded-lg py-2 pl-7 pr-2 text-left transition-colors hover:bg-muted/60 ${FOCUS_RING}`}
                >
                  {body}
                </button>
              ) : (
                <div className="flex items-center gap-3 py-2 pl-7">{body}</div>
              )}
            </div>
            {insideChip && (
              <div className="flex items-center gap-2 pl-7 py-1">
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">Inside for {insideChip}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Stands in for the sighting that should sit at this point in the sequence. Rendered
 * hollow and dashed so it never reads as something the system actually recorded, and
 * carries the jump into the face log for the window it belongs to.
 */
function MissingEventNode({ anomaly, from, to, onFind }: {
  anomaly: PatternAnomaly; from: TimelineEvent | null; to: TimelineEvent;
  /** Omitted when the reader can't read the unknown-face feed. */
  onFind?: () => void;
}) {
  const window = from
    ? `between ${fmtClock(from.seen_at)} and ${fmtClock(to.seen_at)}`
    : `before ${fmtClock(to.seen_at)}`;
  return (
    <div className="relative flex items-start gap-3 py-2 pl-7">
      {/* Hollow ring, not a filled dot: nothing was recorded here. */}
      <span
        className="absolute left-[2px] top-[18px] h-2.5 w-2.5 rounded-full shrink-0 border-2"
        style={{ borderColor: ANOMALY_COLOR, backgroundColor: 'var(--card)' }}
      />
      <div
        className="rounded-md border border-dashed px-3 py-2"
        style={{ borderColor: ANOMALY_COLOR, backgroundColor: `color-mix(in srgb, ${ANOMALY_COLOR} 8%, transparent)` }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={tint(ANOMALY_COLOR)}>
            <ExclamationTriangleIcon className="h-3 w-3" /> {missingLabel(anomaly.missing)}
          </span>
          <span className="text-sm font-medium text-foreground whitespace-nowrap">{window}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {anomalyLabel(anomaly)}. A camera most likely saw it but couldn&apos;t match the face, so it was filed as unknown.
        </p>
        {onFind && (
          <button
            onClick={onFind}
            className="mt-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors hover:brightness-95"
            style={tint(ANOMALY_COLOR)}
          >
            Find in face log →
          </button>
        )}
      </div>
    </div>
  );
}

/** Lazy face-crop thumbnail for a timeline event (fetched via the event-image proxy). */
function EventPhoto({ eventId }: { eventId: string | null }) {
  const { attendance } = useApi();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!eventId) { setFailed(true); return; }
    (async () => {
      const url = await attendance.eventImageDataUrl(eventId, 'faceCrop');
      if (alive) { if (url) setSrc(url); else setFailed(true); }
    })();
    return () => { alive = false; };
  }, [attendance, eventId]);
  if (failed || !eventId) return <div className="h-10 w-14 rounded bg-muted shrink-0" />;
  if (!src) return <div className="h-10 w-14 rounded bg-muted animate-pulse shrink-0" />;
  return <img src={src} alt="" className="h-10 w-14 rounded object-cover shrink-0 border border-border" />;
}
